import React, { useEffect, useRef } from 'react';

// --- CONFIGURABLE CONSTANTS ---
const CONFIG = {
  // Power curve: Higher values (e.g. 3.5) make peaks rarer and silence deeper.
  SPIKE_FACTOR: 3.5,

  // Minimum bar height (0.05 = 5%). Preserves "silence" but keeps bars visible.
  MIN_BAR_HEIGHT: 0.05,

  // Radius of the bar corners in pixels.
  ROUNDED_CORNERS: 2,

  // Smoothing: How much of the previous bar to keep?
  // 0.0 = No smoothing (jagged), 0.9 = Flat line. 0.4 is a good balance.
  SMOOTHING_FACTOR: 0.4,

  // COLOR GENERATION SETTINGS (HSL)
  COLOR: {
    BASE_HUE: 200, // Starting Hue (Cyan/Blue)
    HUE_RANGE: 30, // Variance (200 to 230)
    BASE_SAT: 70, // Minimum Saturation %
    SAT_RANGE: 20, // Variance (70% to 90%)
    BASE_LIGHT: 45, // Minimum Lightness %
    LIGHT_RANGE: 15, // Variance (45% to 60%)
  },
};

interface DeterministicWaveformProps {
  seed: string; // Unique ID to generate the shape and color
  color?: string; // Optional: Force a specific color (overrides auto-gen)
  bars?: number; // Total number of bars to attempt drawing
  barGap?: number; // Space between bars in pixels
  height?: number; // Max height in pixels
  className?: string; // Tailwind classes
}

/**
 * 1. Simple Hash Function (String -> Integer)
 */
const hashCode = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

/**
 * 2. Mulberry32 PRNG (Seeded Random Number Generator)
 */
const mulberry32 = (a: number) => {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

/**
 * 3. Color Generator (Deterministic HSL)
 * Generates a unique shade of blue based on the seed.
 */
const generateColor = (seedString: string) => {
  const hash = hashCode(seedString);
  const { BASE_HUE, HUE_RANGE, BASE_SAT, SAT_RANGE, BASE_LIGHT, LIGHT_RANGE } = CONFIG.COLOR;

  // Use bitwise shifts to get different "random" parts of the hash for H/S/L
  const hue = BASE_HUE + (Math.abs(hash) % HUE_RANGE);
  const sat = BASE_SAT + (Math.abs(hash >> 8) % SAT_RANGE);
  const light = BASE_LIGHT + (Math.abs(hash >> 16) % LIGHT_RANGE);

  return `hsl(${hue}, ${sat}%, ${light}%)`;
};

export const DeterministicWaveform: React.FC<DeterministicWaveformProps> = ({
  seed,
  color: userColor, // Rename prop to differentiate from internal var
  bars = 50,
  barGap = 1,
  height = 40,
  className = '',
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = () => {
      // 1. Setup Size & Scaling
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      const width = rect.width;
      const drawHeight = rect.height;

      if (width <= 0 || drawHeight <= 0) return;

      // 2. Initialize RNG
      const seedNum = hashCode(seed);
      const random = mulberry32(seedNum);

      // 3. Calculate Layout
      const minWidthForAllBars = (bars - 1) * barGap;
      const effectiveBars =
        width >= minWidthForAllBars
          ? bars
          : Math.max(1, Math.floor((width + barGap) / (1 + barGap)));

      const barWidthComputed = (width - (effectiveBars - 1) * barGap) / effectiveBars;

      if (barWidthComputed <= 0) return;

      // 4. Configure Canvas
      canvas.width = width * dpr;
      canvas.height = drawHeight * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);

      // 5. Pick Color (User override OR Auto-generated Blue)
      const finalColor = userColor || generateColor(seed);

      const drawBandHeight = Math.min(height, drawHeight);
      ctx.clearRect(0, 0, width, drawHeight);
      ctx.fillStyle = finalColor;

      // 6. Draw with Smoothing
      let prevHeight = drawBandHeight * CONFIG.MIN_BAR_HEIGHT;

      for (let i = 0; i < effectiveBars; i++) {
        const rawRandom = random();

        // Calculate Target Height (Spiky)
        const dynamicFactor = Math.pow(rawRandom, CONFIG.SPIKE_FACTOR);
        const targetHeight = Math.max(
          drawBandHeight * CONFIG.MIN_BAR_HEIGHT,
          drawBandHeight * dynamicFactor,
        );

        // Apply Smoothing (Blend with previous bar)
        // Formula: (Prev * 0.4) + (Target * 0.6)
        const smoothHeight =
          prevHeight * CONFIG.SMOOTHING_FACTOR + targetHeight * (1 - CONFIG.SMOOTHING_FACTOR);

        // Update history
        prevHeight = smoothHeight;

        // Render
        const x = i * (barWidthComputed + barGap);
        const y = (drawBandHeight - smoothHeight) / 2;

        ctx.beginPath();
        if (ctx.roundRect) {
          ctx.roundRect(x, y, barWidthComputed, smoothHeight, CONFIG.ROUNDED_CORNERS);
        } else {
          ctx.rect(x, y, barWidthComputed, smoothHeight);
        }
        ctx.fill();
      }
    };

    draw();

    const ro = new ResizeObserver(() => draw());
    ro.observe(canvas);

    return () => ro.disconnect();
  }, [seed, userColor, bars, barGap, height]);

  return (
    <div className={className} style={{ width: '100%', height: '100%', minHeight: 0 }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
    </div>
  );
};
